import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostBinding,
  inject,
  input,
  Input,
  OnChanges,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Store } from '@ngrx/store';
import { combineLatest, first, of } from 'rxjs';

import { DatasetInfo, ProjectRoleName } from '@blaze-shared/api-contracts-domain';
import {
  fromRouter,
  generalSettingsSelectors,
  ProjectContextFacade,
} from '@blaze-shared/app-state-data-access';
import { AuthService } from '@blaze-shared/auth-data-access';
import { hasProjectRole } from '@blaze-shared/auth-domain';
import { TablesApiService } from '@blaze-shared/datasets-data-access';
import { BlazeTable } from '@blaze-shared/datasets-domain';
import { DialogService } from '@blaze-shared/shared-feature';
import {
  OptionalAction,
  OptionalActionsComponent,
  TooltipDirective,
} from '@blaze-shared/shared-ui';

@Component({
  selector: 'blaze-dataset-toggle',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dataset-toggle.component.html',
  styleUrls: ['./dataset-toggle.component.scss'],
  imports: [
    FormsModule,
    MatIcon,
    MatMenuModule,
    MatSlideToggleModule,
    OptionalActionsComponent,
    TooltipDirective,
  ],
})
export class DatasetToggleComponent implements OnChanges, OnInit {
  private readonly authService = inject(AuthService);
  private readonly dialogService = inject(DialogService);
  private readonly projectContextFacade = inject(ProjectContextFacade);
  private readonly store = inject(Store);
  private readonly tablesApiService = inject(TablesApiService);

  dataset = input<DatasetInfo | BlazeTable>();

  changed = output<boolean>();
  deleteDataset = output();
  editDataset = output();
  refreshDatasets = output();
  showPreview = output();

  @Input() actionName = '';
  @Input() actions: 'delete' | 'share' = 'delete';
  @Input() isBlazeTable = false;
  @Input() isLink = false;
  @Input() isSelected = false;
  @Input() previewOnly = false;
  @Input() showMenuOptions = false;

  @Input() isChangeTracking: boolean | undefined;

  moreOpen = signal(false);
  canDelete = signal(false);
  canShare = signal(false);
  isShared = signal(false);
  projectId = signal<string | undefined>(undefined);
  projectGroupId?: string;

  actionsList = computed(() => [
    <OptionalAction>{
      label: this.isShared() ? 'Stop Sharing within Group' : 'Share Within Group',
      icon: 'globe',
      iconColorMenu: 'var(--primary-darker-color)',
      visible: this.canShare(),
      execute: (event: Event) => {
        this.toggleShare();
        event.stopPropagation();
      },
      cy: `DatasetToggle:${this.isShared() ? 'unshare' : 'share'}`,
    },
    <OptionalAction>{
      label: 'Delete Table',
      icon: 'icon_trash_16',
      iconColorMenu: 'var(--warn-color)',
      visible: this.canDelete(),
      execute: (event: Event) => {
        this.deleteDataset.emit();
        event.stopPropagation();
      },
      cy: 'DatasetToggle:delete',
    },
  ]);

  name = computed(() => {
    const dataset = this.dataset();
    const ownerProjectId = (dataset && 'projectId' in dataset && dataset.projectId) || undefined;
    if (ownerProjectId && ownerProjectId !== this.projectId() && dataset?.name) {
      // Dataset linked from a different project
      let suffix = '';
      if ('projectName' in dataset && dataset.projectName) {
        suffix = ` (${dataset.projectName})`;
      }
      return dataset?.name + suffix;
    }
    return dataset?.name || this.actionName || 'Unknown Name';
  });

  @HostBinding('class.have-change-tracking') get haveChangeTracking(): boolean {
    return !!(this.dataset() as BlazeTable)?.changeTracking?.datasetId;
  }

  get isSystemTable(): boolean {
    const dataset = this.dataset();
    return dataset && 'systemTableType' in dataset ? !!dataset.systemTableType : false;
  }

  get isRootUser(): boolean {
    return this.authService.isCurrentUserAccountRoot();
  }

  ngOnChanges(): void {
    const isRegularTable = this.isBlazeTable && !this.isLink && !this.isSystemTable;
    const dataset = this.dataset();
    const ownerProjectId = (dataset && 'projectId' in dataset && dataset.projectId) || undefined;
    const foreignProjectAccess =
      ownerProjectId === this.projectId() ||
      hasProjectRole(
        this.projectContextFacade.userInfo,
        ProjectRoleName.ProjectEditor,
        ownerProjectId ?? null,
      );

    this.canDelete.set(
      isRegularTable && (!this.isChangeTracking || this.isRootUser) && foreignProjectAccess,
    );
    this.canShare.set(
      this.isBlazeTable && !this.isLink && !!this.projectGroupId && foreignProjectAccess,
    );

    this.isShared.set(
      (dataset && 'isSharedWithProjectGroup' in dataset && dataset.isSharedWithProjectGroup) ||
        false,
    );
  }

  ngOnInit(): void {
    combineLatest({
      projectId: this.store.select(fromRouter.getProjectId),
      projectGroupId: this.store.select(generalSettingsSelectors.selectProjectGroupId),
    })
      .pipe(first())
      .subscribe(({ projectId, projectGroupId }) => {
        this.projectId.set(projectId);
        this.projectGroupId = projectGroupId;
        this.ngOnChanges();
      });
  }

  editIfBlazeTable(): void {
    if (this.isBlazeTable) {
      this.editDataset.emit();
    }
  }

  notifyToggleChanged(): void {
    this.changed.emit(this.isSelected);
  }

  toggleShare(): void {
    const dataset = this.dataset();
    const confirm$ = this.isShared()
      ? this.dialogService
          .openConfirmDialog({
            title: `Stop Sharing ${dataset && 'name' in dataset ? dataset.name : ''} ?`,
            description:
              'Are you sure you want to stop sharing this table? Workflows and UI components that rely on this shared table will immediately lose access and may stop working as expected.',
            confirmLabel: 'Stop Sharing',
          })
          .afterClosed()
      : of(true);

    confirm$.subscribe((confirm) => {
      const dataset = this.dataset();
      if (!confirm || !dataset) {
        return;
      }
      this.tablesApiService.setProjectGroupFeatures(dataset.id, !this.isShared()).subscribe(() => {
        this.refreshDatasets.emit();
      });
    });
  }
}
